import axios from 'axios'
import { z } from 'zod'

import type { Options, Version } from './types'

const isIPv4 = z.string().ip({ version: 'v4' })
const isIPv6 = z.string().ip({ version: 'v6' })

const defaults = {
  timeout: 5000,
  onlyHttps: false,
}

class IpNotFoundError extends Error {
  constructor(options: { cause: unknown }) {
    super(`Could not get the public IP address ${options.cause}`)
    this.name = 'IpNotFoundError'
  }
}

const type = {
  v4: {
    httpsUrls: ['https://icanhazip.com/', 'https://api.ipify.org/'],
  },
  v6: {
    httpsUrls: ['https://icanhazip.com/', 'https://api6.ipify.org/'],
  },
}

const queryHttps = async (version: Version, options: Options) => {
  const requestOptions = {
    dnsLookupIpVersion: version === 'v6' ? 6 : 4,
    retry: {
      limit: 0,
    },
    timeout: options.timeout,
  }

  const urls = [...type[version].httpsUrls, ...(options.fallbackUrls ?? [])]

  let lastError: unknown

  for (const url of urls) {
    try {
      const response = await axios.get(url, requestOptions)

      const ip = (response.data || '').trim()

      const method = version === 'v6' ? isIPv6 : isIPv4

      if (ip && method.parse(ip)) {
        return ip
      }
    } catch (error) {
      lastError = error
    }
  }

  throw new IpNotFoundError({ cause: lastError })
}

export const publicIpv4 = (options?: Options) => {
  options = {
    ...defaults,
    ...options,
  }

  return queryHttps('v4', options)
}

export const publicIpv6 = (options?: Options) => {
  options = {
    ...defaults,
    ...options,
  }

  return queryHttps('v6', options)
}
